/**
 * MonsterPlacementPhase Component
 *
 * Renders the monster placement phase (Phase 2). Players alternately place
 * 5 monsters on their half of the board. Same board + top bar layout as
 * IslandPlacementPhase.
 *
 * Used by:
 * - GamePage (rendered when gamePhase === 'MONSTER_PLACEMENT')
 *
 * Responsibilities:
 * - Display board for monster placement
 * - Display monster selection panel (MonsterList)
 * - Handle monster selection
 * - Handle square selection for placement
 * - Validate monster placement
 * - Submit monster placement
 * - Show whose turn it is
 *
 * Props (when used as controlled from GamePage):
 * - roomCode: string
 */

import React, { useMemo, useCallback, useState } from 'react';
import { useAuth } from '../../AuthProvider';
import { useGameStateContext } from '../context/useGameStateContext';
import useMonsters from '../hooks/useMonsters';
import { useBoard } from '../hooks/useBoard';
import useGameSocket from '../hooks/useGameSocket';
import { Board } from '../components/Board/Board.tsx';
import { MonsterList } from '../components/Monsters/MonsterList.tsx';
import { LeftSideDrawer } from '../components/UI/SideDrawer';
import { GameProfile } from '../components/UI/GameProfile';
import { parseBoardState } from '../utils/boardHelpers';
import { getBackground } from '../../utils/images';
import type { PlayerColor } from '../types/game.types';
import type { MonsterDefinition } from '../types/monster.types';
import './GamePlay.css';
import MonsterPanel from '../components/Monsters/MonsterPanel.tsx';

export interface MonsterPlacementPhaseProps {
    /** Room code/name */
    roomCode: string;
}

export const MonsterPlacementPhase: React.FC<MonsterPlacementPhaseProps> = () => {
    const { user } = useAuth();
    const {
        board,
        myPlayerLetter,
        theme,
        playerA,
        playerB,
        isMyTurn,
    } = useGameStateContext();
    const { selectedMonster, setSelectedMonster, myMonsters, oppMonsters } = useMonsters();
    const { handleSquareClick, selectedSquare, possibleMoves, setSelectedSquare } = useBoard();
    const { placeMonster } = useGameSocket();

    const [placeError, setPlaceError] = useState<string | null>(null);
    const [isPlacing, setIsPlacing] = useState(false);

    /** Base squares from server board; empty if board not yet loaded */
    const squares = useMemo(() => {
        if (!board) return [];
        return parseBoardState(board);
    }, [board]);

    /** In monster placement, show the selected monster on the selected tile (preview) */
    const displaySquares = useMemo(() => {
        if (
            !selectedSquare ||
            !selectedMonster ||
            !myPlayerLetter
        ) {
            return squares;
        }
        return squares.map((sq) =>
            sq.row === selectedSquare.row && sq.col === selectedSquare.col
                ? {
                    ...sq,
                    type: 'monster' as const,
                    player: myPlayerLetter,
                    value: selectedMonster.type,
                }
                : sq
        );
    }, [squares, selectedSquare, selectedMonster, myPlayerLetter]);

    /** Monster type IDs already placed (on board) this game */
    const placedMonsterTypeIds = useMemo(
        () =>
            myMonsters
                .filter(
                    (m) =>
                        m.position &&
                        !(m.position.row === -1 && m.position.col === -1)
                )
                .map((m) => m.type),
        [myMonsters]
    );

    const playerAColor: PlayerColor = (playerA?.color as PlayerColor) ?? 'red';
    const playerBColor: PlayerColor = (playerB?.color as PlayerColor) ?? 'blue';
    const myMonstersColor: PlayerColor = myPlayerLetter === 'A' ? playerAColor : playerBColor;
    const oppMonstersColor: PlayerColor = myPlayerLetter === 'A' ? playerBColor : playerAColor;

    const handleMonsterSelect = useCallback(
        (def: MonsterDefinition | null) => {
            if (!myPlayerLetter) return;
            setSelectedMonster(
                def
                    ? {
                        id: def.id,
                        type: def.id,
                        name: def.name,
                        player: myPlayerLetter,
                    }
                    : null
            );
        },
        [myPlayerLetter, setSelectedMonster]
    );

    /** Number of monsters already placed this game (on board); next slot is this + 1 */
    const placedCount = useMemo(
        () =>
            myMonsters.filter(
                (m) =>
                    m.position &&
                    !(m.position.row === -1 && m.position.col === -1)
            ).length,
        [myMonsters]
    );

    const handlePlaceMonster = useCallback(() => {
        if (!selectedMonster || !selectedSquare || !myPlayerLetter || isPlacing) return;
        setPlaceError(null);
        setIsPlacing(true);
        const monsterId = placedCount + 1; // next slot 1–5
        placeMonster(
            {
                monsterId,
                monsterTypeId: selectedMonster.type,
                row: selectedSquare.row,
                col: selectedSquare.col,
            },
            (response) => {
                setIsPlacing(false);
                if (response.success) {
                    setSelectedSquare(null);
                    setSelectedMonster(null);
                } else {
                    setPlaceError(response.error ?? 'Failed to place monster');
                }
            }
        );
    }, [
        selectedMonster,
        selectedSquare,
        myPlayerLetter,
        isPlacing,
        placedCount,
        placeMonster,
        setSelectedSquare,
        setSelectedMonster,
    ]);

    const canPlace =
        isMyTurn &&
        selectedMonster != null &&
        selectedSquare != null &&
        placedCount < 5 &&
        !isPlacing;

    if (!board) {
        return (
            <div
                className="gameplay_container"
                style={{
                    backgroundImage: `url(${getBackground(theme)})`,
                    backgroundSize: 'cover',
                }}
            >
                <p className="gameplay_status">Loading board...</p>
            </div>
        );
    }

    return (
        <div
            className="gameplay_container"
            style={{
                backgroundImage: `url(${getBackground(theme)})`,
                backgroundSize: 'cover',
            }}
        >
            <GameProfile username={user.username} playerLetter={myPlayerLetter} />

            <div className="gameplay_main">
                <p className="gameplay_instructions">
                    {`Place your monsters on your half of the board. Select a monster, then click a square.`}
                </p>
                <div className="gameplay_board_and_monsters_container">
                    <MonsterPanel
                        monsters={myMonsters}
                        mine={true}
                        playerColor={myMonstersColor}
                        theme={theme}
                    />
                    <div className="gameplay_board_wrapper">
                        <Board
                            squares={displaySquares}
                            onSquareClick={handleSquareClick}
                            selectedSquare={selectedSquare}
                            possibleMoves={possibleMoves}
                            theme={theme}
                            playerLetter={myPlayerLetter}
                            playerAColor={playerAColor}
                            playerBColor={playerBColor}
                            disabled={false}
                        />
                    </div>
                    <MonsterPanel
                        monsters={oppMonsters}
                        mine={false}
                        playerColor={oppMonstersColor}
                        theme={theme}
                    />
                </div>

                <div className="gameplay_submit_section">
                    {placeError && (
                        <p className="gameplay_status gameplay_error" role="alert">
                            {placeError}
                        </p>
                    )}
                    <button
                        type="button"
                        className="gameplay_button"
                        onClick={handlePlaceMonster}
                        disabled={!canPlace}
                    >
                        {isPlacing ? 'Placing...' : 'Place Monster'}
                    </button>
                </div>
            </div>

            <LeftSideDrawer title="Monsters" defaultOpen={true}>
                <MonsterList
                    selectedMonster={selectedMonster}
                    onMonsterSelect={handleMonsterSelect}
                    placedMonsterTypeIds={placedMonsterTypeIds}
                    theme={theme}
                    isMyTurn={isMyTurn}
                />
            </LeftSideDrawer>
        </div>
    );
};

export default MonsterPlacementPhase;
